import { useNow } from '@/lib/infra/sharedClock';
import { cn } from '@/lib/infra/utils';
import {
  QUEUE_DISPLAY_MAX_AGE_MS,
  type QueueSnapshotStatus,
} from '@/hooks/useUserKalshiPOS';

export function queueSnapshotIsHardFailure(
  status: QueueSnapshotStatus | null | undefined,
): boolean {
  return (
    status != null &&
    !status.fresh &&
    (status.retained ||
      status.consecutiveFailures > 0 ||
      status.fetchedAt == null ||
      status.reportedAt == null ||
      status.staleAfterMs <= 0)
  );
}

export function queueAgeMs(status: QueueSnapshotStatus, nowMs: number): number | null {
  if (status.receivedAtMonoMs != null) return status.ageMs == null ? null :
    status.ageMs + Math.max(0, performance.now() - status.receivedAtMonoMs);
  return status.fetchedAt == null ? null : Math.max(0, nowMs - status.fetchedAt);
}

export function queueVintageLabel(status: QueueSnapshotStatus, nowMs: number): string {
  const age = queueAgeMs(status, nowMs);
  if (age == null) return 'Queue update age unknown';
  const ageSeconds = Math.floor(age / 1000);
  return `Queue updated ${ageSeconds}s ago`;
}

export function queueSourceGapBreaksContinuity(
  previousFetchedAt: number | null,
  currentFetchedAt: number | null,
): boolean {
  if (previousFetchedAt == null || currentFetchedAt == null) return false;
  const gapMs = currentFetchedAt - previousFetchedAt;
  return gapMs < 0 || gapMs > QUEUE_DISPLAY_MAX_AGE_MS;
}

/** Identity of the physical queue read, not the number of push envelopes that
 * happened to carry it. Legacy relays have no source timestamp and retain the
 * pre-metadata snapshot sequence fallback. */
export function queueSourceSampleToken(
  status: QueueSnapshotStatus | null | undefined,
  fallbackSnapshotSeq: number,
): string {
  return status?.fetchedAt != null
    ? `f:${status.fetchedAt}`
    : `s:${fallbackSnapshotSeq}`;
}

export type QueueSourceKind = 'relay' | 'legacy';

export function queueSourceKind(
  status: QueueSnapshotStatus | null | undefined,
): QueueSourceKind {
  return status?.fetchedAt != null ? 'relay' : 'legacy';
}

/** Quiet, always-present provenance for human-facing last-confirmed queue
 * values. Its footprint and normal color do not blink on every freshness
 * boundary; only a real failed/retained read promotes it to amber. */
export function QueueVintageBadge({
  status,
  className,
}: {
  status: QueueSnapshotStatus | null | undefined;
  className?: string;
}) {
  const nowMs = useNow();
  if (status == null) return null;
  const hardFailure = queueSnapshotIsHardFailure(status);
  const age = queueAgeMs(status, nowMs);
  // Safety consumers fail closed immediately, but a single 250ms retained
  // response must not make the entire table blink amber. Promote the fixed-
  // footprint badge only once the last confirmed source has crossed the same
  // stale boundary the queue contract already exposes. A cold/no-vintage
  // failure remains immediate because there is no confirmed value to show.
  const visibleHardFailure =
    hardFailure &&
    (age == null || age > status.staleAfterMs);
  const delayed =
    age != null && age > Math.max(10_000, status.staleAfterMs * 4);
  const vintageLabel = queueVintageLabel(status, nowMs);
  const ageSeconds =
    age == null ? null : Math.floor(age / 1000);
  const label =
    visibleHardFailure || delayed
      ? ageSeconds == null
        ? 'Queue age unknown'
        : `Queue ${ageSeconds}s old`
      : 'Queue position';
  return (
    <span
      data-testid="queue-vintage"
      className={cn(
        'rounded border px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
        visibleHardFailure || delayed
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
          : 'border-border/70 bg-muted/40 text-muted-foreground',
        className,
      )}
      title={
        status.perOrder
          ? `Queue freshness is checked per order; ${vintageLabel.toLowerCase()}; only orders without fresh evidence pause their queue alerts`
          : status.fresh && !delayed
          ? `Queue positions are current; ${vintageLabel.toLowerCase()}`
          : `Showing the last confirmed queue positions for reference; ${vintageLabel.toLowerCase()}; queue alerts pause until a fresh response arrives`
      }
    >
      {label}
    </span>
  );
}
